import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  ResponsiveContainer,
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";
import { ImSpinner2 } from "react-icons/im";
import {
  FiTrendingUp, FiTrendingDown, FiShoppingBag, FiUsers,
  FiPackage, FiCheckCircle, FiXCircle, FiClock,
  FiStar, FiDollarSign, FiBarChart2, FiAward,
} from "react-icons/fi";
import styles from "../../styles/statisticsPanel.module.css";

// ── Constants ──────────────────────────────────────────────────────────────
const PERIODS = [
  { label: "٣٠ يوماً",  value: 30  },
  { label: "٩٠ يوماً",  value: 90  },
  { label: "١٨٠ يوماً", value: 180 },
  { label: "سنة كاملة", value: 365 },
];

const STATUS_AR = {
  new:               "جديد",
  preparing:         "تحضير",
  printing:          "طباعة",
  pressing:          "كبس",
  wrapping:          "تغليف",
  "out for delivery":"للتوصيل",
  delivered:         "مُسلَّم",
  cancelled:         "ملغي",
};
const STATUS_COLOR = {
  new:               "#3b82c4",
  preparing:         "#e07c2a",
  printing:          "#7C5CBF",
  pressing:          "#2E9E85",
  wrapping:          "#C4547A",
  "out for delivery":"#8c6f3d",
  delivered:         "#2f9e57",
  cancelled:         "#e0533d",
};
const MATERIAL_COLOR = { matte: "#9B773D", gloss: "#c98a52" };
const MATERIAL_AR    = { matte: "مط", gloss: "لامع" };
const PAYMENT_AR     = { "cash on delivery": "نقدي", "credit card": "بطاقة" };
const PAYMENT_COLOR  = { "cash on delivery": "#2f9e57", "credit card": "#3b82c4" };
const VAN_AR         = { small: "سيارة صغيرة", big: "سيارة كبيرة" };
const VAN_COLOR      = { small: "#c98a52", big: "#7C5CBF" };
const STAR_COLOR     = "#f59e0b";
const CHART_ACCENT   = "#c98a52";
const CHART_SOFT     = "#e7c9b7";
const GRID_COLOR     = "#efe4d6";

// ── Helpers ────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n ?? 0).toLocaleString("ar-EG", { maximumFractionDigits: 2 });
const fmtShort = (n) => {
  const v = Number(n ?? 0);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}م`;
  if (v >= 1000)    return `${(v / 1000).toFixed(1)}ك`;
  return String(v);
};
const trendPct = (now, prev) => {
  if (!prev) return null;
  return Math.round(((now - prev) / prev) * 100);
};

const shortDate = (dateStr, period) => {
  const d = new Date(dateStr);
  if (period <= 30)  return `${d.getDate()}/${d.getMonth() + 1}`;
  if (period <= 90)  return `${d.getDate()}/${d.getMonth() + 1}`;
  return `${d.getMonth() + 1}/${d.getFullYear().toString().slice(-2)}`;
};

// ── Custom Tooltip ─────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <p className={styles.tooltipLabel}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className={styles.tooltipItem}>
          {p.name}: {isCurrency ? `${fmt(p.value)} ₪` : fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

// ── Donut custom label ─────────────────────────────────────────────────────
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: "0.72rem", fontWeight: 700, direction: "ltr" }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, unit, trend, trendLabel, accent, sub }) => {
  const isUp = trend > 0;
  const isDown = trend < 0;
  return (
    <div className={styles.kpiCard} style={{ "--accent": accent ?? CHART_ACCENT }}>
      <div className={styles.kpiIconWrap}>
        <Icon className={styles.kpiIcon} />
      </div>
      <div className={styles.kpiBody}>
        <span className={styles.kpiLabel}>{label}</span>
        <span className={styles.kpiValue}>
          {value}{unit && <span className={styles.kpiUnit}> {unit}</span>}
        </span>
        {sub && <span className={styles.kpiSub}>{sub}</span>}
      </div>
      {trend != null && (
        <div className={`${styles.kpiTrend} ${isUp ? styles.kpiTrendUp : isDown ? styles.kpiTrendDown : styles.kpiTrendFlat}`}>
          {isUp ? <FiTrendingUp /> : isDown ? <FiTrendingDown /> : null}
          <span>{Math.abs(trend)}%</span>
          {trendLabel && <span className={styles.kpiTrendLabel}>{trendLabel}</span>}
        </div>
      )}
    </div>
  );
};

// ── Section Title ──────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <h3 className={styles.sectionTitle}>
    <span className={styles.sectionTitleLine} />
    {children}
  </h3>
);

// ── Donut Legend ───────────────────────────────────────────────────────────
const DonutLegend = ({ items }) => (
  <div className={styles.donutLegend}>
    {items.map((item, i) => (
      <div key={i} className={styles.donutLegendItem}>
        <span className={styles.donutLegendDot} style={{ background: item.color }} />
        <span className={styles.donutLegendLabel}>{item.label}</span>
        <span className={styles.donutLegendVal}>{item.value}</span>
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
//  StatisticsPanel
// ═══════════════════════════════════════════════════════════════════════════
const StatisticsPanel = () => {
  const [period, setPeriod]   = useState(30);
  const [data,   setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const token = useSelector(s => s.auth?.token);

  const authCfg = useCallback(() => ({
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/statistics/dashboard?days=${period}`,
        authCfg(),
      );
      setData(res.data?.data ?? null);
    } catch {
      toast.error(<span className="toast">لم نتمكن من تحميل الإحصائيات</span>);
    } finally {
      setLoading(false);
    }
  }, [period, authCfg]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <ImSpinner2 className={styles.spinner} />
        <span>جارٍ تحميل الإحصائيات…</span>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, ordersByStatus, ordersByPayment, ordersByMaterial,
          ordersByChannel, topAreas, reviewDistribution, topProducts,
          ordersTimeline, vanBreakdown } = data;

  // ── Derived trends ───────────────────────────────────────────────────────
  const revenueTrend = trendPct(kpis.revenueThisMonth, kpis.revenueLastMonth);
  const ordersTrend  = trendPct(kpis.ordersThisMonth,  kpis.ordersLastMonth);

  // ── Timeline data — thin out x-labels ───────────────────────────────────
  const timelineWithLabel = ordersTimeline.map((d, i) => ({
    ...d,
    xLabel: i % Math.ceil(ordersTimeline.length / 10) === 0 ? shortDate(d.date, period) : "",
  }));

  // ── Status donut ─────────────────────────────────────────────────────────
  const statusDonut = ordersByStatus.map(s => ({
    name:  STATUS_AR[s.status] ?? s.status,
    value: s.count,
    color: STATUS_COLOR[s.status] ?? "#aaa",
  })).filter(s => s.value > 0);

  // ── Material donut ───────────────────────────────────────────────────────
  const materialDonut = ordersByMaterial.map(m => ({
    name:  MATERIAL_AR[m.material] ?? m.material,
    value: m.count,
    color: MATERIAL_COLOR[m.material] ?? CHART_ACCENT,
  })).filter(m => m.value > 0);

  // ── Payment donut ─────────────────────────────────────────────────────────
  const paymentDonut = ordersByPayment.map(p => ({
    name:  PAYMENT_AR[p.payment] ?? p.payment,
    value: p.count,
    color: PAYMENT_COLOR[p.payment] ?? CHART_ACCENT,
  })).filter(p => p.value > 0);

  // ── Van donut ─────────────────────────────────────────────────────────────
  const vanDonut = vanBreakdown.map(v => ({
    name:  VAN_AR[v.vanType] ?? v.vanType,
    value: v.count,
    color: VAN_COLOR[v.vanType] ?? CHART_ACCENT,
  })).filter(v => v.value > 0);

  // ── Reviews bar ──────────────────────────────────────────────────────────
  const reviewsBar = [1, 2, 3, 4, 5].map(s => {
    const found = reviewDistribution.find(r => r.stars === s);
    return { stars: `${"★".repeat(s)}`, count: found?.count ?? 0 };
  });

  // ── Channels bar ─────────────────────────────────────────────────────────
  const channelsBar = ordersByChannel.map(ch => ({
    name:  `${ch.icon ?? ""}${ch.name}`,
    count: ch.count,
    color: ch.color ?? CHART_ACCENT,
  }));

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={styles.wrapper}>

      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <FiBarChart2 className={styles.headerIcon} />
          <span>الإحصائيات</span>
        </div>
        <div className={styles.periodBtns}>
          {PERIODS.map(p => (
            <button
              key={p.value}
              className={`${styles.periodBtn}${period === p.value ? ` ${styles.periodBtnActive}` : ""}`}
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className={styles.kpiGrid}>
        <KpiCard
          icon={FiDollarSign} label="إجمالى الإيرادات"
          value={`${fmt(kpis.totalRevenue)} ₪`}
          trend={revenueTrend}
          trendLabel="مقارنة بالشهر الماضي"
          sub={`هذا الشهر: ${fmt(kpis.revenueThisMonth)} ₪`}
          accent="#9B773D"
        />
        <KpiCard
          icon={FiShoppingBag} label="إجمالى الطلبات"
          value={fmt(kpis.totalOrders)}
          trend={ordersTrend}
          trendLabel="مقارنة بالشهر الماضي"
          sub={`هذا الشهر: ${kpis.ordersThisMonth} طلب`}
          accent="#c98a52"
        />
        <KpiCard
          icon={FiClock} label="طلبات نشطة"
          value={fmt(kpis.activeOrders)}
          accent="#e07c2a"
        />
        <KpiCard
          icon={FiCheckCircle} label="طلبات مكتملة"
          value={fmt(kpis.completedOrders)}
          accent="#2f9e57"
        />
        <KpiCard
          icon={FiUsers} label="العملاء"
          value={fmt(kpis.totalCustomers)}
          sub={`${kpis.newCustomersThisMonth} عميل جديد هذا الشهر`}
          accent="#3b82c4"
        />
        <KpiCard
          icon={FiPackage} label="اللوحات"
          value={fmt(kpis.totalProducts)}
          accent="#7C5CBF"
        />
        <KpiCard
          icon={FiAward} label="متوسط قيمة الطلب"
          value={`${fmt(kpis.avgOrderValue)} ₪`}
          accent="#C4547A"
        />
        <KpiCard
          icon={FiStar} label="متوسط التقييم"
          value={kpis.avgRating > 0 ? `${kpis.avgRating} ★` : "—"}
          sub={`${fmt(kpis.totalReviews)} تقييم`}
          accent={STAR_COLOR}
        />
      </div>

      {/* ── Secondary stats row ── */}
      <div className={styles.secondaryRow}>
        <div className={styles.secondaryCard}>
          <span className={styles.secondaryVal}>{fmt(kpis.cancelledOrders)}</span>
          <span className={styles.secondaryLabel}>طلبات ملغاة</span>
          <FiXCircle className={styles.secondaryIcon} style={{ color: "#e0533d" }} />
        </div>
        <div className={styles.secondaryCard}>
          <span className={styles.secondaryVal}>{fmt(kpis.specialOrders)}</span>
          <span className={styles.secondaryLabel}>طلبات خاصة</span>
          <FiAward className={styles.secondaryIcon} style={{ color: "#7C5CBF" }} />
        </div>
        <div className={styles.secondaryCard}>
          <span className={styles.secondaryVal}>{fmt(kpis.totalReviews)}</span>
          <span className={styles.secondaryLabel}>تقييمات</span>
          <FiStar className={styles.secondaryIcon} style={{ color: STAR_COLOR }} />
        </div>
        <div className={styles.secondaryCard}>
          <span className={styles.secondaryVal}>{kpis.ordersLastMonth}</span>
          <span className={styles.secondaryLabel}>طلبات الشهر الماضي</span>
          <FiShoppingBag className={styles.secondaryIcon} />
        </div>
      </div>

      {/* ── Timeline ── */}
      {ordersTimeline.length > 0 && (
        <>
          <SectionTitle>الطلبات عبر الزمن — آخر {period} يوم</SectionTitle>
          <div className={styles.chartRow}>
            <div className={styles.chartCard}>
              <p className={styles.chartLabel}>عدد الطلبات</p>
              <div style={{ direction: "ltr" }}>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={timelineWithLabel} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={CHART_ACCENT} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={CHART_ACCENT} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                    <XAxis dataKey="xLabel" tick={{ fontSize: 11, fill: "#a08a6b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#a08a6b" }} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="count" name="طلبات"
                      stroke={CHART_ACCENT} fill="url(#gradOrders)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className={styles.chartCard}>
              <p className={styles.chartLabel}>الإيرادات (₪)</p>
              <div style={{ direction: "ltr" }}>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={timelineWithLabel} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#9B773D" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#9B773D" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                    <XAxis dataKey="xLabel" tick={{ fontSize: 11, fill: "#a08a6b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#a08a6b" }} tickFormatter={fmtShort} />
                    <Tooltip content={<ChartTooltip isCurrency />} />
                    <Area type="monotone" dataKey="revenue" name="إيرادات"
                      stroke="#9B773D" fill="url(#gradRevenue)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Donuts row ── */}
      <SectionTitle>توزيع الطلبات</SectionTitle>
      <div className={styles.donutRow}>

        {/* Status donut */}
        {statusDonut.length > 0 && (
          <div className={styles.donutCard}>
            <p className={styles.chartLabel}>حالة الطلبات</p>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusDonut} cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90}
                    dataKey="value" labelLine={false} label={renderCustomLabel}>
                    {statusDonut.map((s, i) => <Cell key={i} fill={s.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <DonutLegend items={statusDonut.map(s => ({ color: s.color, label: s.name, value: s.value }))} />
          </div>
        )}

        {/* Material donut */}
        {materialDonut.length > 0 && (
          <div className={styles.donutCard}>
            <p className={styles.chartLabel}>المادة (عدد القطع)</p>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={materialDonut} cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90}
                    dataKey="value" labelLine={false} label={renderCustomLabel}>
                    {materialDonut.map((m, i) => <Cell key={i} fill={m.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <DonutLegend items={materialDonut.map(m => ({ color: m.color, label: m.name, value: m.value }))} />
          </div>
        )}

        {/* Payment donut */}
        {paymentDonut.length > 0 && (
          <div className={styles.donutCard}>
            <p className={styles.chartLabel}>طريقة الدفع</p>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={paymentDonut} cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90}
                    dataKey="value" labelLine={false} label={renderCustomLabel}>
                    {paymentDonut.map((p, i) => <Cell key={i} fill={p.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <DonutLegend items={paymentDonut.map(p => ({ color: p.color, label: p.name, value: p.value }))} />
          </div>
        )}

        {/* Van type donut */}
        {vanDonut.length > 0 && (
          <div className={styles.donutCard}>
            <p className={styles.chartLabel}>نوع السيارة</p>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={vanDonut} cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90}
                    dataKey="value" labelLine={false} label={renderCustomLabel}>
                    {vanDonut.map((v, i) => <Cell key={i} fill={v.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <DonutLegend items={vanDonut.map(v => ({ color: v.color, label: v.name, value: v.value }))} />
          </div>
        )}
      </div>

      {/* ── Top Products ── */}
      {topProducts.length > 0 && (
        <>
          <SectionTitle>أعلى ١٠ منتجات بالإيراد</SectionTitle>
          <div className={styles.chartCard} style={{ width: "100%" }}>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={Math.max(260, topProducts.length * 38)}>
                <BarChart data={topProducts} layout="vertical"
                  margin={{ top: 4, right: 60, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#a08a6b" }} tickFormatter={fmtShort} />
                  <YAxis type="category" dataKey="name" width={140}
                    tick={{ fontSize: 11, fill: "#6f5630", direction: "rtl", textAnchor: "start" }} />
                  <Tooltip
                    formatter={(v, n) => [n === "revenue" ? `${fmt(v)} ₪` : v, n === "revenue" ? "إيراد" : "كمية"]}
                  />
                  <Legend formatter={v => v === "revenue" ? "الإيراد (₪)" : "الكمية"} />
                  <Bar dataKey="revenue" name="revenue" fill={CHART_ACCENT} radius={[0, 4, 4, 0]} />
                  <Bar dataKey="qty"     name="qty"     fill={CHART_SOFT}   radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ── Areas + Channels ── */}
      <div className={styles.chartRow}>
        {topAreas.length > 0 && (
          <div className={styles.chartCard}>
            <SectionTitle>أعلى مناطق التوصيل</SectionTitle>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={Math.max(220, topAreas.length * 36)}>
                <BarChart data={topAreas} layout="vertical"
                  margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#a08a6b" }} />
                  <YAxis type="category" dataKey="area" width={90}
                    tick={{ fontSize: 11, fill: "#6f5630" }} />
                  <Tooltip formatter={v => [v, "عدد الطلبات"]} />
                  <Bar dataKey="count" name="count" fill="#2E9E85" radius={[0, 4, 4, 0]}>
                    {topAreas.map((_, i) => (
                      <Cell key={i} fill={`hsl(${160 + i * 8}, 55%, ${48 - i * 2}%)`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {channelsBar.length > 0 && (
          <div className={styles.chartCard}>
            <SectionTitle>قنوات البيع</SectionTitle>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={Math.max(220, channelsBar.length * 48)}>
                <BarChart data={channelsBar} layout="vertical"
                  margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#a08a6b" }} />
                  <YAxis type="category" dataKey="name" width={110}
                    tick={{ fontSize: 11, fill: "#6f5630" }} />
                  <Tooltip formatter={v => [v, "عدد الطلبات"]} />
                  <Bar dataKey="count" name="count" radius={[0, 4, 4, 0]}>
                    {channelsBar.map((ch, i) => (
                      <Cell key={i} fill={ch.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* ── Reviews distribution ── */}
      {kpis.totalReviews > 0 && (
        <>
          <SectionTitle>توزيع التقييمات — متوسط {kpis.avgRating} ★</SectionTitle>
          <div className={styles.chartCard} style={{ maxWidth: 480, margin: "0 auto 1.5rem" }}>
            <div style={{ direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={reviewsBar} margin={{ top: 4, right: 10, left: -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                  <XAxis dataKey="stars" tick={{ fontSize: 12, fill: STAR_COLOR }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#a08a6b" }} />
                  <Tooltip formatter={v => [v, "عدد التقييمات"]} />
                  <Bar dataKey="count" fill={STAR_COLOR} radius={[4, 4, 0, 0]}>
                    {reviewsBar.map((_, i) => (
                      <Cell key={i} fill={`hsl(${38 + i * 6}, ${80 - i * 5}%, ${55 + i * 3}%)`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default StatisticsPanel;
